import React, { useState, useEffect, useRef, useMemo } from 'react';
import { tripService } from '../../services/tripService';
import { bookingApi } from '../../services/bookingApi';
import { bookingService } from '../../services/bookingService';
import { referralService } from '../../services/referralService';
import { useAuth } from '../../auth/AuthContext';
import { Trip, TripSeat } from '../../types';
import { getSeatDefinition, findNextSequentialSeat } from '../../constants/seatLayout';
import { useRouter, Link } from '../../router/Router';
import {
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
  Timer,
  Bus,
  Sparkles,
  MapPin,
  Calendar,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { BookingStepper } from '../booking/BookingStepper';

export const SeatSelection: React.FC<{ tripId: string }> = ({ tripId }) => {
  const { user, studentProfile } = useAuth();
  const { navigate } = useRouter();

  const [trip, setTrip] = useState<Trip | null>(null);
  const [_seats, setSeats] = useState<TripSeat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSeatNumber, setSelectedSeatNumber] = useState<number | null>(null);
  const [paymentMethod] = useState<'CASH' | 'ONLINE'>('CASH');
  const [isHolding, setIsHolding] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [holdSecondsRemaining, setHoldSecondsRemaining] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isBusFull, setIsBusFull] = useState(false);

  // Check if current student has a 10% referral discount on this trip's month
  const discountInfo = useMemo(() => {
    if (!trip?.date || !studentProfile?.referralStats) {
      return { hasDiscount: false, discountPercent: 0, formattedMonth: '' };
    }
    return referralService.checkTripDiscount(studentProfile.referralStats, trip.date);
  }, [trip?.date, studentProfile?.referralStats]);

  const effectivePrice = useMemo(() => {
    if (!trip) return 0;
    if (discountInfo.hasDiscount) {
      return Math.round(trip.price * (1 - discountInfo.discountPercent / 100));
    }
    return trip.price;
  }, [trip, discountInfo]);

  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoHoldRequestedRef = useRef(false);

  // Load Trip and subscribe to live seat updates & auto-assign seat
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const initData = async () => {
      setIsLoading(true);
      const tripData = await tripService.getTripById(tripId);
      setTrip(tripData);

      const seatList = await tripService.getTripSeats(tripId);
      setSeats(seatList);

      // 1. Check if user already has an active hold saved in sessionStorage
      let existingSeat: number | null = null;
      try {
        const savedHoldRaw = sessionStorage.getItem(`nashgo_hold_${tripId}`);
        if (savedHoldRaw) {
          const savedHold = JSON.parse(savedHoldRaw);
          if (savedHold?.seatNumber && savedHold?.holdExpiresAt) {
            const expires = new Date(savedHold.holdExpiresAt).getTime();
            const remaining = Math.max(0, Math.floor((expires - Date.now()) / 1000));
            if (remaining > 0) {
              existingSeat = savedHold.seatNumber;
              setSelectedSeatNumber(savedHold.seatNumber);
              setHoldSecondsRemaining(remaining);
            } else {
              sessionStorage.removeItem(`nashgo_hold_${tripId}`);
            }
          }
        }
      } catch (e) {
        // Ignored
      }

      // 2. If no active hold, automatically find the NEXT sequential seat (1, 2, 3... 50)
      if (!existingSeat && !autoHoldRequestedRef.current) {
        autoHoldRequestedRef.current = true;
        const autoSeat = findNextSequentialSeat(seatList, tripData?.capacity || 50);

        if (autoSeat) {
          setSelectedSeatNumber(autoSeat);
          setIsHolding(true);
          try {
            const holdRes = await bookingApi.requestSeatHold({ tripId, seatNumber: autoSeat });
            if (holdRes.success && holdRes.data) {
              const remaining = holdRes.data.remainingSeconds || 300;
              setHoldSecondsRemaining(remaining);
              try {
                sessionStorage.setItem(
                  `nashgo_hold_${tripId}`,
                  JSON.stringify({ seatNumber: autoSeat, holdExpiresAt: holdRes.data.holdExpiresAt })
                );
              } catch (err) {
                // Ignored
              }
            } else if (holdRes.error) {
              setErrorMessage(holdRes.error.messageAr);
            }
          } catch (holdErr) {
            console.warn('Auto hold error:', holdErr);
          } finally {
            setIsHolding(false);
          }
        } else {
          setIsBusFull(true);
        }
      }

      // Real-time subscription to seats subcollection
      unsubscribe = tripService.subscribeTripSeats(tripId, (updatedSeats) => {
        setSeats(updatedSeats);
      });

      setIsLoading(false);
    };

    initData();

    return () => {
      if (unsubscribe) unsubscribe();
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [tripId, user?.uid]);

  // Countdown timer for 5-minute hold
  useEffect(() => {
    if (holdSecondsRemaining !== null && holdSecondsRemaining > 0) {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

      countdownIntervalRef.current = setInterval(() => {
        setHoldSecondsRemaining((prev) => {
          if (prev === null || prev <= 1) {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            setErrorMessage('انتهت مدة تثبيت المقعد المخصص لك (5 دقائق). يرجى تحديث الصفحة للحصول على المقعد التالي.');
            try {
              sessionStorage.removeItem(`nashgo_hold_${tripId}`);
            } catch (e) {
              // Ignored
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [holdSecondsRemaining, tripId]);

  // Format seconds to mm:ss
  const formatTimer = (totalSeconds: number): string => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Final Booking Confirmation Handler
  const handleConfirmBooking = async () => {
    if (!user || !selectedSeatNumber || isConfirming) return;

    if (holdSecondsRemaining === 0) {
      setErrorMessage('انتهت مدة تثبيت المقعد. يرجى تحديث الصفحة لإعادة تعيين المقعد.');
      return;
    }

    setIsConfirming(true);
    setErrorMessage(null);

    try {
      const res = await bookingService.confirmBooking({
        tripId,
        seatNumber: selectedSeatNumber,
        paymentMethod,
        discountApplied: discountInfo.hasDiscount,
        discountPercent: discountInfo.discountPercent,
        finalPrice: effectivePrice,
        studentName: studentProfile?.fullName,
        studentPhone: studentProfile?.phoneNumber,
      });

      if (res.success && res.data) {
        // Record referral booking if user was invited by another student
        if (user.uid) {
          referralService.recordBookingForReferral(user.uid, res.data.bookingId).catch(console.warn);
        }
        try {
          sessionStorage.removeItem(`nashgo_hold_${tripId}`);
        } catch (e) {
          // Ignored
        }
        // Navigate to payment checkout screen
        navigate(`/student/payment/${res.data.bookingId}`);
      } else if (res.error) {
        setErrorMessage(res.error.messageAr);
      }
    } catch (err) {
      setErrorMessage('تعذر تأكيد الحجز. يرجى التحقق من اتصال الإنترنت وإعادة المحاولة.');
    } finally {
      setIsConfirming(false);
    }
  };

  if (isLoading) {
    return (
      <div className="booking-page-container" dir="rtl">
        <div className="loading-state-card">
          <div className="loading-spinner"></div>
          <p>جاري تعيين مقعدك تلقائياً بالترتيب في الحافلة...</p>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="booking-page-container" dir="rtl">
        <div className="empty-state-card">
          <h3>الرحلة غير موجودة</h3>
          <Link to="/student/universities" className="btn-primary">
            العودة للجامعات
          </Link>
        </div>
      </div>
    );
  }

  const selectedDef = selectedSeatNumber ? getSeatDefinition(selectedSeatNumber) : null;

  return (
    <div className="booking-page-container seat-selection-page" dir="rtl">
      {/* Mobile Stepper Indicator */}
      <BookingStepper currentStep={2} />

      {/* Header & Breadcrumbs */}
      <div className="booking-page-header">
        <div className="header-breadcrumbs">
          <Link to="/student" className="breadcrumb-link">
            لوحة الطالب
          </Link>
          <span className="breadcrumb-sep">/</span>
          <Link to={`/student/trips?universityId=${trip.universityId}`} className="breadcrumb-link">
            رحلات الجامعة
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">تأكيد حجز المقعد</span>
        </div>

        <div className="seat-page-banner">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-bold mb-2 border border-blue-200">
              <Sparkles size={14} />
              <span>تعيين المقعد آلياً بالترتيب</span>
            </div>
            <h1 className="page-title">تأكيد حجز المقعد المخصص لك</h1>
            <p className="page-subtitle">
              رحلة: <strong>{trip.universityName}</strong> | موعد التحرك:{' '}
              <strong>{trip.departureTime} صباحًا</strong> ({trip.date})
            </p>
          </div>
          <div className="trip-price-pill">
            {discountInfo.hasDiscount ? (
              <div className="text-left">
                <span className="text-[11px] line-through text-slate-400 block font-mono">{trip.price} ج.م</span>
                <span className="pill-price text-emerald-600 font-bold">{effectivePrice}</span>
                <span className="pill-curr text-emerald-600 font-bold">ج.م</span>
                <span className="block text-[10px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded mt-0.5 text-center">
                  خصم {discountInfo.discountPercent}%
                </span>
              </div>
            ) : (
              <>
                <span className="pill-price">{trip.price}</span>
                <span className="pill-curr">ج.م</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Active Referral Discount Banner */}
      {discountInfo.hasDiscount && (
        <div className="p-3.5 rounded-2xl bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border border-emerald-300 text-emerald-950 mb-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold flex-shrink-0 shadow-sm">
              <Sparkles size={20} />
            </div>
            <div>
              <strong className="text-sm font-bold block text-emerald-900">
                مكافأة دعوة الأصدقاء مفعلة! 🎉 (خصم {discountInfo.discountPercent}%)
              </strong>
              <span className="text-xs text-emerald-700">
                تطبق مكافأة الدعوة على رحلات شهر ({discountInfo.formattedMonth}) بنجاح.
              </span>
            </div>
          </div>
          <div className="text-left font-bold text-xs bg-white/80 border border-emerald-200 px-3 py-1.5 rounded-xl shadow-xs">
            <span className="text-slate-500 block text-[10px]">قيمة الوفر:</span>
            <span className="text-emerald-700 text-sm font-mono">{trip.price - effectivePrice} ج.م</span>
          </div>
        </div>
      )}

      {/* Automatic Assignment Info Alert */}
      <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border border-blue-200 text-slate-800 mb-4 flex items-start gap-3 shadow-sm">
        <div className="p-2 rounded-xl bg-blue-600 text-white flex-shrink-0 mt-0.5">
          <Bus size={20} />
        </div>
        <div>
          <strong className="text-sm text-blue-900 block mb-0.5">
            نظام التعيين التلقائي للمقاعد بأولوية الحجز
          </strong>
          <p className="text-xs text-slate-600 leading-relaxed m-0">
            يقوم نظام OTO STUDENTS بتعيين المقاعد آلياً بالترتيب التصاعدي (1، 2، 3 ...) وفقاً لأسبقية الحجز وتاريخ التسجيل، لضمان انسيابية وراحة صعود الطلاب دون الحاجة للاختيار اليدوي.
          </p>
        </div>
      </div>

      {/* Live Hold Timer Banner */}
      {selectedSeatNumber && holdSecondsRemaining !== null && holdSecondsRemaining > 0 && (
        <div className="seat-hold-live-banner" role="status">
          <Timer className="timer-icon pulse-animation" size={22} />
          <div className="timer-text-content">
            <strong>تم تثبيت المقعد رقم {selectedSeatNumber} مؤقتًا باسمك</strong>
            <span>
              متبقي <code>{formatTimer(holdSecondsRemaining)}</code> دقيقة لتأكيد الحجز قبل تحرير المقعد
            </span>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {errorMessage && (
        <div className="auth-error-banner" role="alert">
          <AlertCircle size={20} className="auth-error-icon" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Main Seat Confirmation Card */}
      {isBusFull ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-rose-200 shadow-sm max-w-xl mx-auto space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-rose-50 text-rose-600 flex items-center justify-center font-bold">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-lg font-bold text-slate-900">عذرًا، اكتملت جميع مقاعد هذه الحافلة</h2>
          <p className="text-xs text-slate-600 leading-relaxed max-w-md mx-auto">
            تم حجز جميع المقاعد ({trip?.totalSeats || trip?.capacity || 50} مقعداً) لهذه الرحلة بالكامل. يمكنك العودة واختيار موعد آخر أو تسجيل طلب رحلة إضافية.
          </p>
          <div className="pt-2">
            <Link to="/student/universities" className="btn-primary inline-flex items-center gap-2">
              <ArrowRight size={16} />
              <span>استعراض الرحلات الأخرى</span>
            </Link>
          </div>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Seat Spotlight Box */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 left-0 h-2 bg-gradient-to-r from-blue-600 to-indigo-600"></div>

            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">
              المقعد المخصص لك في الرحلة ({trip?.totalSeats || trip?.capacity || 50} راكباً)
            </span>

            <div className="inline-flex items-center justify-center gap-3 p-4 px-8 rounded-2xl bg-blue-50 border-2 border-blue-200 shadow-inner my-2">
              <div className="text-4xl font-extrabold text-blue-700 font-mono">
                #{selectedSeatNumber || '—'}
              </div>
              <div className="text-right border-r border-blue-200 pr-3">
                <strong className="block text-sm font-bold text-slate-900">
                  {selectedDef?.labelAr || `المقعد ${selectedSeatNumber}`}
                </strong>
                <span className="text-xs text-blue-600 font-medium">
                  {selectedSeatNumber && (trip?.capacity || trip?.totalSeats || 50) <= 16
                    ? selectedSeatNumber <= 3
                      ? 'في الصف الأمامي'
                      : selectedSeatNumber <= 9
                      ? 'في وسط المركبة'
                      : 'في الكنبة الخلفية'
                    : selectedSeatNumber && selectedSeatNumber <= 12
                    ? 'في الصفوف الأمامية'
                    : selectedSeatNumber && selectedSeatNumber <= 36
                    ? 'في وسط الحافلة'
                    : 'في الصفوف الخلفية'}
                </span>
              </div>
            </div>

            <p className="text-xs text-emerald-700 font-bold mt-2 flex items-center justify-center gap-1.5">
              <CheckCircle2 size={16} />
              <span>تم التعيين الآلي بنجاح ومقعدك مؤكد ومحفوظ باسمك</span>
            </p>
          </div>

          {/* Trip Summary Details */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
            <h3 className="text-sm font-bold text-slate-800 pb-2 border-b border-slate-100 flex items-center gap-2">
              <Bus size={16} className="text-blue-600" />
              <span>تفاصيل الرحلة والمسار</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
                <span className="text-slate-500 flex items-center gap-1">
                  <MapPin size={13} className="text-blue-600" />
                  <span>نقطة التحرك بالإسكندرية:</span>
                </span>
                <strong className="text-slate-900 block font-bold">
                  {trip.origin || trip.pickupLocation}
                </strong>
                <span className="text-slate-500 block font-mono">
                  الساعة {trip.departureTime} صباحًا
                </span>
              </div>

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
                <span className="text-slate-500 flex items-center gap-1">
                  <MapPin size={13} className="text-emerald-600" />
                  <span>الجامعة والكلية الوجهة:</span>
                </span>
                <strong className="text-slate-900 block font-bold">
                  {trip.destination || trip.destinationDetails || trip.universityName}
                </strong>
                <span className="text-slate-500 block">وصول مباشر للحرم الجامعي</span>
              </div>

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
                <span className="text-slate-500 flex items-center gap-1">
                  <Calendar size={13} className="text-purple-600" />
                  <span>تاريخ السفر:</span>
                </span>
                <strong className="text-slate-900 block font-bold">{trip.date}</strong>
                <span className="text-slate-500 block">
                  {(trip.capacity || trip.totalSeats || 50) <= 16
                    ? 'ميكروباص / هاي اس مكيف'
                    : (trip.capacity || trip.totalSeats || 50) <= 32
                    ? 'ميني باص سياحي مكيف'
                    : 'حافلة سياحية مكيفة'}{' '}
                  ({trip.capacity || trip.totalSeats || 50} مقعداً)
                </span>
              </div>

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 space-y-1">
                <span className="text-slate-500 flex items-center gap-1">
                  <Clock size={13} className="text-amber-600" />
                  <span>قيمة التذكرة:</span>
                </span>
                {discountInfo.hasDiscount ? (
                  <div className="flex items-baseline gap-2">
                    <strong className="text-emerald-700 text-sm block font-bold font-mono">
                      {effectivePrice} ج.م
                    </strong>
                    <span className="text-xs line-through text-slate-400 font-mono">
                      {trip.price} ج.م
                    </span>
                    <span className="text-[10px] text-emerald-800 bg-emerald-100 font-bold px-1.5 py-0.5 rounded">
                      خصم 10%
                    </span>
                  </div>
                ) : (
                  <strong className="text-blue-900 text-sm block font-bold font-mono">
                    {trip.price} ج.م
                  </strong>
                )}
                <span className="text-slate-500 block">شاملة المقعد المخصص والانتقال المباشر</span>
              </div>
            </div>

            {/* Payment Methods Info */}
            <div className="pt-2 text-xs">
              <span className="text-slate-500 block mb-1.5 font-medium">وسائل السداد المتاحة:</span>
              <div className="flex flex-wrap gap-1.5">
                <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 font-bold">
                  ⚡ تطبيق إنستاباي (InstaPay)
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 font-bold">
                  📱 فودافون كاش والمحافظ الإلكترونية
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 font-bold">
                  🏢 السداد نقداً بالفرع
                </span>
              </div>
            </div>

            {/* Confirmation CTA Button */}
            <div className="pt-3">
              <button
                type="button"
                className="w-full btn-primary py-3 px-6 text-sm font-bold flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all"
                disabled={!selectedSeatNumber || isConfirming || isHolding}
                onClick={handleConfirmBooking}
              >
                {isConfirming ? (
                  <>
                    <span className="loading-spinner"></span>
                    <span>جارٍ تأكيد الحجز والانتقال للسداد...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck size={20} />
                    <span>تأكيد حجز المقعد (#{selectedSeatNumber}) ومتابعة الدفع ({effectivePrice} ج.م)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
